/**
 * RUM dei Web Vitals sul PostHog esistente (T110-INT).
 *
 * Contratto dell'SDK installato (posthog-js 1.373.4), verificato nel sorgente:
 * - `capture_performance: { web_vitals: true }` abilita localmente la cattura senza
 *   configurazione remota (extensions/web-vitals/index.js, getter `isEnabled`);
 * - ogni evento `$web_vitals` porta, per metrica, `$web_vitals_<M>_value` e
 *   `$web_vitals_<M>_event` (oggetto con name, value, delta, id, rating, navigationType,
 *   entries, attribution, $current_url, ...) (stesso file, `_flushToCapture`).
 *
 * Attribuzione: $pathname e' il percorso del documento di ingresso (landing). INP/CLS maturati dopo
 * una navigazione interna alla SPA restano attribuiti alla famiglia della landing (limite noto).
 *
 * Qui NON si aggiungono observer: si configura quello dell'SDK e si minimizza
 * l'evento in `before_send`. Gli altri eventi passano invariati.
 */
import type { BeforeSendFn, CaptureResult, PostHogConfig } from 'posthog-js';

export const WEB_VITALS_METRICS = ['LCP', 'CLS', 'FCP', 'INP'] as const;

/** Campi del dettaglio per metrica che servono all'aggregato; il resto (entries, attribution) cade. */
const METRIC_FIELDS_KEPT = ['name', 'value', 'delta', 'id', 'rating', 'navigationType'] as const;

/**
 * Allow-list delle proprietà di un evento $web_vitals; tutto il resto cade. Servono all'ingestione:
 * token, distinct_id ($insert_id, $time, $lib, $lib_version, $is_identified, $process_person_profile)
 * aggiunti dall'SDK in posthog-core.js (calculateEventProperties, ~1084-1092; getEventProperties in
 * utils/event-utils.js:269-308); $session_id/$window_id per il raggruppamento; $device_type, $host,
 * $pathname, $current_url per la query; app_build e display_mode registrati da main.tsx.
 * NON passano: $session_entry_*, utm_*, $referrer, $referring_domain, $initial_*, $set*, $search_engine,
 * $ph_keyword, user agent, lingua, schermo, ecc.
 */
const ALLOWED_PROPERTIES = new Set([
  'token',
  'distinct_id',
  '$insert_id',
  '$time',
  '$lib',
  '$lib_version',
  '$process_person_profile',
  '$is_identified',
  '$session_id',
  '$window_id',
  '$device_type',
  '$host',
  '$pathname',
  '$current_url',
  'app_build',
  'display_mode',
]);
/** Campi di primo livello dell'evento che restano; $set e $set_once di primo livello cadono. */
const ALLOWED_EVENT_FIELDS = ['uuid', 'event', 'timestamp'] as const;

const ROOT_PATH = '/';
const METRIC_KEY = /^\$web_vitals_(LCP|CLS|FCP|INP)_(value|event)$/;

/** Percorso del documento senza origine, query né hash; sempre con una sola `/` iniziale. */
export function normalizeDocumentPath(raw: string): string {
  if (typeof raw !== 'string' || raw === '') return ROOT_PATH;
  let path = raw;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return ROOT_PATH;
    }
  }
  const cut = path.search(/[?#]/);
  if (cut !== -1) path = path.slice(0, cut);
  path = path.replace(/^\/+/, '');
  return `/${path}`;
}

function originOf(url: unknown): string | undefined {
  if (typeof url !== 'string') return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

function minimiseMetric(detail: unknown, normalizedUrl: string | undefined): unknown {
  if (typeof detail !== 'object' || detail === null) return detail;
  const source = detail as Record<string, unknown>;
  const kept: Record<string, unknown> = {};
  for (const field of METRIC_FIELDS_KEPT) {
    if (field in source) kept[field] = source[field];
  }
  if (normalizedUrl !== undefined) kept.$current_url = normalizedUrl;
  return kept;
}

function minimiseVitals(event: CaptureResult, initialPath: string): CaptureResult {
  const input = (event.properties ?? {}) as Record<string, unknown>;
  const origin = originOf(input.$current_url);
  const normalizedUrl = origin ? `${origin}${initialPath}` : undefined;
  const properties: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(input)) {
    const metricKey = METRIC_KEY.exec(key);
    if (metricKey) {
      properties[key] = metricKey[2] === 'event' ? minimiseMetric(value, normalizedUrl) : value;
    } else if (ALLOWED_PROPERTIES.has(key)) {
      properties[key] = value;
    }
  }

  if ('$pathname' in properties || normalizedUrl !== undefined) properties.$pathname = initialPath;
  if (normalizedUrl !== undefined) properties.$current_url = normalizedUrl;

  const result: Record<string, unknown> = {};
  const source = event as unknown as Record<string, unknown>;
  for (const field of ALLOWED_EVENT_FIELDS) {
    if (field in source) result[field] = source[field];
  }
  result.properties = properties;
  return result as unknown as CaptureResult;
}

/**
 * `before_send` che minimizza SOLO `$web_vitals`: percorso iniziale del documento (non quello
 * al momento dell'invio), niente query/hash, niente entries DOM né attribution.
 * Ogni altro evento viene restituito identico (stesso riferimento).
 */
export function createWebVitalsBeforeSend(initialPathRaw: string): BeforeSendFn {
  const initialPath = normalizeDocumentPath(initialPathRaw);
  return (event) => {
    if (!event || event.event !== '$web_vitals') return event;
    return minimiseVitals(event, initialPath);
  };
}

/**
 * Configurazione di init di PostHog. Consenso e persistenza restano quelli storici:
 * memoria + profili solo per utenti identificati; l'upgrade a localStorage+cookie avviene
 * in ConsentContext e non cambia.
 */
export function buildPostHogConfig(options: {
  apiHost: string;
  initialPath: string;
}): Partial<PostHogConfig> {
  return {
    api_host: options.apiHost,
    defaults: '2026-01-30',
    // Conteggio anonimo dalla prima visita SENZA scrivere cookie/localStorage.
    persistence: 'memory',
    person_profiles: 'identified_only',
    // Forzato in locale: non dipende dalla configurazione remota del progetto.
    capture_performance: {
      web_vitals: true,
      web_vitals_attribution: false,
      web_vitals_allowed_metrics: [...WEB_VITALS_METRICS],
    },
    before_send: createWebVitalsBeforeSend(options.initialPath),
  };
}
